// Cache keys, fetchers, and invalidation for the customer workspace.
//
// Every read the customer list and workspace make goes through `queryClient` under
// one of these keys, so:
//   - a list row seeds its customer, and the workspace header renders on arrival;
//   - hovering a row (or a tab) warms exactly what the next screen reads first;
//   - a write invalidates only the resources it changed, not the whole page.
//
// Key shape: ["admin", "customer", id]              the customer record
//            ["admin", "customer", id, resource]    one of its histories
//            ["admin", "customers", filters]        a list view (first pages loaded)
import type {
  AdminAccessCode,
  AdminInvoice,
  EntitlementAccessLevel,
  EntitlementCustomer,
  ManagedEntitlement,
  SubscriptionChange
} from "$lib/api/contract";
import {
  getEntitlementCustomer,
  listEntitlementCustomers,
  type EntitlementCustomerQuery,
  type EntitlementCustomerSort,
  type SortDirection,
  listAdminAccessCodes,
  listAdminInvoices,
  listManagedEntitlements,
  listSubscriptionChanges
} from "$lib/api/endpoints";
import { listAdminQuotes, type AdminQuoteWithActivity } from "$lib/api/quotes";
import { queryClient, type QueryConfig, type QueryKey } from "$lib/query/cache.svelte";
import type { CursorPage } from "$lib/query/cursor.svelte";
import { preloadAdminPage } from "./admin-pages";
import { fetchCustomerSubscription, type AdminCustomerSubscription } from "./customer-subscription";

export type CustomerResource = "entitlements" | "invoices" | "quotes" | "changes" | "access-codes" | "subscription";

export const CUSTOMER_LIST_PATH = "/admin/entitlements";

export function customerKey(customerId: string): QueryKey {
  return ["admin", "customer", customerId];
}

export function customerResourceKey(customerId: string, resource: CustomerResource): QueryKey {
  return ["admin", "customer", customerId, resource];
}

export const CUSTOMERS_KEY: QueryKey = ["admin", "customers"];

// --------------------------------------------------------------- list filters

export type CustomerStatusFilter = "all" | "active" | "trial" | "suspended" | "expired";
export type CustomerPlanFilter = "all" | "community" | "pro" | "team" | "builders" | "enterprise";
export type CustomerAccessFilter = "all" | EntitlementAccessLevel;

export interface CustomerListFilters {
  query: string;
  status: CustomerStatusFilter;
  plan: CustomerPlanFilter;
  access: CustomerAccessFilter;
  sort: EntitlementCustomerSort;
  direction: SortDirection;
}

export const CUSTOMER_LIST_PAGE_SIZE = 50;
export const SORT_FIELDS: readonly EntitlementCustomerSort[] = ["name", "externalKey", "status", "updatedAt"];
export const STATUS_FILTERS: readonly CustomerStatusFilter[] = ["all", "active", "trial", "suspended", "expired"];
export const PLAN_FILTERS: readonly CustomerPlanFilter[] = ["all", "community", "pro", "team", "builders", "enterprise"];
export const ACCESS_FILTERS: readonly CustomerAccessFilter[] = [
  "all", "enterprise", "builder", "team", "pro", "community", "consumer", "beta", "alpha"
];

export function defaultDirection(sort: EntitlementCustomerSort): SortDirection {
  return sort === "updatedAt" ? "desc" : "asc";
}

function pick<T extends string>(allowed: readonly T[], value: string | null, fallback: T): T {
  return allowed.find((candidate) => candidate === value) ?? fallback;
}

/** The list's filters as the URL states them; unknown values fall back to defaults. */
export function filtersFromQuery(query: URLSearchParams): CustomerListFilters {
  const sort = pick(SORT_FIELDS, query.get("sort"), "updatedAt");
  return {
    query: query.get("query")?.trim() ?? "",
    status: pick(STATUS_FILTERS, query.get("status"), "all"),
    plan: pick(PLAN_FILTERS, query.get("plan"), "all"),
    access: pick(ACCESS_FILTERS, query.get("access"), "all"),
    sort,
    direction: pick(["asc", "desc"] as const, query.get("direction"), defaultDirection(sort))
  };
}

/** Defaults drop out, so the plain list keeps a clean URL. */
export function filtersToQuery(filters: CustomerListFilters): Record<string, string | null> {
  return {
    query: filters.query || null,
    status: filters.status === "all" ? null : filters.status,
    plan: filters.plan === "all" ? null : filters.plan,
    access: filters.access === "all" ? null : filters.access,
    sort: filters.sort === "updatedAt" ? null : filters.sort,
    direction: filters.direction === defaultDirection(filters.sort) ? null : filters.direction
  };
}

// The list endpoint takes `accessLevel` (OpenAPI), which `EntitlementCustomerQuery`
// does not declare yet; it is forwarded as-is until `$lib/api` adds it.
type CustomerListRequest = EntitlementCustomerQuery & { accessLevel?: EntitlementAccessLevel };

export function fetchCustomerPage(
  filters: CustomerListFilters,
  cursor: string | undefined,
  signal: AbortSignal
): Promise<CursorPage<EntitlementCustomer>> {
  const request: CustomerListRequest = {
    limit: CUSTOMER_LIST_PAGE_SIZE,
    ...(cursor ? { cursor } : {}),
    ...(filters.query ? { query: filters.query } : {}),
    ...(filters.status !== "all" ? { status: filters.status } : {}),
    ...(filters.plan !== "all" ? { plan: filters.plan } : {}),
    ...(filters.access !== "all" ? { accessLevel: filters.access } : {}),
    sort: filters.sort,
    direction: filters.direction
  };
  return listEntitlementCustomers(request, signal);
}

export function customerListKey(filters: CustomerListFilters): QueryKey {
  return ["admin", "customers", { ...filters }];
}

/**
 * Page one of a list view. Each page read seeds its rows' customer entries, so opening
 * any row renders its header without waiting on the network.
 */
export function customerListQuery(filters: CustomerListFilters): QueryConfig<CursorPage<EntitlementCustomer>> {
  return {
    key: customerListKey(filters),
    fetcher: async (signal) => {
      const page = await fetchCustomerPage(filters, undefined, signal);
      for (const customer of page.items) seedCustomer(customer);
      return page;
    }
  };
}

export const customerQueries = {
  customer: (id: string): QueryConfig<EntitlementCustomer> => ({
    key: customerKey(id),
    fetcher: (signal) => getEntitlementCustomer(id, signal)
  }),
  entitlements: (id: string): QueryConfig<ManagedEntitlement[]> => ({
    key: customerResourceKey(id, "entitlements"),
    fetcher: (signal) => listManagedEntitlements(id, signal)
  }),
  invoices: (id: string): QueryConfig<AdminInvoice[]> => ({
    key: customerResourceKey(id, "invoices"),
    fetcher: (signal) => listAdminInvoices(id, signal)
  }),
  quotes: (id: string): QueryConfig<AdminQuoteWithActivity[]> => ({
    key: customerResourceKey(id, "quotes"),
    fetcher: (signal) => listAdminQuotes(id, signal)
  }),
  changes: (id: string): QueryConfig<SubscriptionChange[]> => ({
    key: customerResourceKey(id, "changes"),
    fetcher: (signal) => listSubscriptionChanges(id, signal)
  }),
  accessCodes: (id: string): QueryConfig<AdminAccessCode[]> => ({
    key: customerResourceKey(id, "access-codes"),
    fetcher: (signal) => listAdminAccessCodes(id, signal)
  }),
  subscription: (id: string): QueryConfig<AdminCustomerSubscription | null> => ({
    key: customerResourceKey(id, "subscription"),
    fetcher: (signal) => fetchCustomerSubscription(id, signal)
  })
};

/** A list row is the same record the workspace reads: render it while revalidating. */
export function seedCustomer(customer: EntitlementCustomer): void {
  queryClient.seed(customerKey(customer.id), customer);
}

/**
 * Warm what the workspace paints first: its chunk, the record, the summary band's
 * reads, and the default Activity tab. Everything else waits for its tab.
 */
export function prefetchCustomerWorkspace(customerId: string, options: { canManageInvoices: boolean }): void {
  preloadAdminPage("admin.customer");
  queryClient.prefetch(customerQueries.customer(customerId));
  queryClient.prefetch(customerQueries.entitlements(customerId));
  queryClient.prefetch(customerQueries.subscription(customerId));
  queryClient.prefetch(customerQueries.changes(customerId));
  if (options.canManageInvoices) queryClient.prefetch(customerQueries.invoices(customerId));
}

/** The workspace's tab id → the read that tab shows. */
export function prefetchCustomerTab(customerId: string, tab: string, options: { canManageInvoices: boolean }): void {
  switch (tab) {
    case "changelog":
      queryClient.prefetch(customerQueries.changes(customerId));
      break;
    case "quotes":
      if (options.canManageInvoices) queryClient.prefetch(customerQueries.quotes(customerId));
      break;
    case "invoices":
      if (options.canManageInvoices) queryClient.prefetch(customerQueries.invoices(customerId));
      break;
    case "entitlements":
      queryClient.prefetch(customerQueries.entitlements(customerId));
      break;
    case "access-codes":
      queryClient.prefetch(customerQueries.accessCodes(customerId));
      break;
  }
}

// ---------------------------------------------------------------- invalidation

/** Every list view: counts, ordering, and the row's own fields may have moved. */
export function invalidateCustomerLists(): void {
  queryClient.invalidate(CUSTOMERS_KEY);
}

export function invalidateCustomerResources(customerId: string, resources: readonly CustomerResource[]): void {
  for (const resource of resources) queryClient.invalidate(customerResourceKey(customerId, resource), { exact: true });
}

/** The record alone (not its histories). */
export function invalidateCustomerRecord(customerId: string): void {
  queryClient.invalidate(customerKey(customerId), { exact: true });
}

/** The record and everything under it, e.g. after a conversion rebinds it. */
export function invalidateCustomer(customerId: string): void {
  queryClient.invalidate(customerKey(customerId));
}

/** A write returned the updated record: show it now, and let the lists catch up. */
export function writeCustomer(customer: EntitlementCustomer): void {
  queryClient.set(customerKey(customer.id), customer);
  invalidateCustomerLists();
}

// ------------------------------------------------------------ list memory

let lastListSearch = "";

/** The list's own query string, so the breadcrumb returns to the same filtered view. */
export function rememberCustomerListSearch(search: string): void {
  lastListSearch = search.startsWith("?") || search === "" ? search : `?${search}`;
}

export function customerListHref(): string {
  return `${CUSTOMER_LIST_PATH}${lastListSearch}`;
}

export type CustomerListPage = CursorPage<EntitlementCustomer>;
